import { useState, useEffect } from 'react';
import { usersAPI, departmentsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users, Loader2, UserPlus, Mail, Lock, User, RefreshCw, Hash, Copy, Check, Edit, Trash2, Home, KeyRound, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '@/lib/backend';
const API_BASE = BACKEND_URL + '/api';

export default function CustomersModule() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [useRandomPassword, setUseRandomPassword] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [resetResult, setResetResult] = useState(null); // {customer, new_password}
  const [resetting, setResetting] = useState(null); // customer id being reset

  // Sorting (defaults to name ascending)
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const getVal = (c) => {
      if (sortField === 'name') return (c.name || '').toLowerCase();
      if (sortField === 'customer_id') return (c.customer_id || '').toLowerCase();
      if (sortField === 'created_at') return c.created_at || '';
      return '';
    };
    const va = getVal(a);
    const vb = getVal(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cyan-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-cyan-600" />;
  };

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    password: '',
    username: '',
    customer_id: '',
    onsite_hourly_rate: '',
    remote_hourly_rate: '',
  });

  const [editData, setEditData] = useState({
    name: '',
    email: '',
    password: '',
    username: '',
    customer_id: '',
    onsite_hourly_rate: '',
    remote_hourly_rate: '',
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.list('customer');
      setCustomers(res.data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = async (forEdit = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/auth/generate-password`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (forEdit) {
        setEditData(prev => ({ ...prev, password: res.data.password }));
      } else {
        setNewCustomer(prev => ({ ...prev, password: res.data.password }));
      }
    } catch (err) {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      if (forEdit) {
        setEditData(prev => ({ ...prev, password }));
      } else {
        setNewCustomer(prev => ({ ...prev, password }));
      }
    }
  };

  const fetchNextCustomerId = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/auth/next-customer-id`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCustomer(prev => ({ ...prev, customer_id: res.data.customer_id }));
      return res.data.customer_id;
    } catch (err) {
      const count = customers.length;
      const fallback = `CUS-${String(count + 1).padStart(5, '0')}`;
      setNewCustomer(prev => ({ ...prev, customer_id: fallback }));
      return fallback;
    }
  };

  const openCreateDialog = async () => {
    // Fetch next ID FIRST and seed the form with it in the same setState
    // call. The previous version reset customer_id='' then set a timeout
    // to fetch — which sometimes lost the fetched value on re-render.
    setError('');
    setSuccess('');
    setUseRandomPassword(false);
    let nextId = '';
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/auth/next-customer-id`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      nextId = res.data?.customer_id || '';
    } catch (err) {
      const count = customers.length;
      nextId = `CUS-${String(count + 1).padStart(5, '0')}`;
    }
    setNewCustomer({
      name: '',
      email: '',
      password: '',
      username: '',
      customer_id: nextId,
      onsite_hourly_rate: '',
      remote_hourly_rate: '',
    });
    setShowCreateDialog(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setEditData({
      name: customer.name,
      email: customer.email,
      password: '',
      username: customer.username || '',
      customer_id: customer.customer_id || '',
      onsite_hourly_rate: customer.onsite_hourly_rate ?? '',
      remote_hourly_rate: customer.remote_hourly_rate ?? '',
    });
    setError('');
    setSuccess('');
    setUseRandomPassword(false);
    setShowEditDialog(true);
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const customerData = {
        name: newCustomer.name,
        email: newCustomer.email?.trim() || undefined,
        password: newCustomer.password,
        role: 'customer',
        username: newCustomer.username || undefined,
        customer_id: newCustomer.customer_id || undefined,
        onsite_hourly_rate: newCustomer.onsite_hourly_rate !== '' ? parseFloat(newCustomer.onsite_hourly_rate) : undefined,
        remote_hourly_rate: newCustomer.remote_hourly_rate !== '' ? parseFloat(newCustomer.remote_hourly_rate) : undefined,
      };

      await axios.post(`${API_BASE}/auth/register`, customerData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`Customer "${newCustomer.name}" created successfully!`);
      loadCustomers();
      
      setTimeout(() => {
        setShowCreateDialog(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Customer creation error:', err);
      let errorMessage = 'Failed to create customer';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    setEditing(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const updatePayload = {
        name: editData.name,
        email: editData.email?.trim() || null,
        username: editData.username || undefined,
        customer_id: editData.customer_id || undefined,
        onsite_hourly_rate: editData.onsite_hourly_rate !== '' ? parseFloat(editData.onsite_hourly_rate) : null,
        remote_hourly_rate: editData.remote_hourly_rate !== '' ? parseFloat(editData.remote_hourly_rate) : null,
      };

      // Only include password if it's been changed
      if (editData.password) {
        updatePayload.password = editData.password;
      }

      await axios.patch(`${API_BASE}/users/${editingCustomer.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`Customer "${editData.name}" updated successfully!`);
      loadCustomers();
      
      setTimeout(() => {
        setShowEditDialog(false);
        setEditingCustomer(null);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Customer update error:', err);
      let errorMessage = 'Failed to update customer';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete customer "${customerName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/users/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Customer deleted successfully`);
      loadCustomers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete customer');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleResetPassword = async (customer) => {
    if (!window.confirm(
      `Reset the password for "${customer.name}"?\n\n` +
      `A new random password will be generated and shown ONCE. Share it with the customer through a secure channel.`
    )) return;
    try {
      setResetting(customer.id);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/users/${customer.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetResult({ customer, new_password: res.data.new_password });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      setTimeout(() => setError(''), 3000);
    } finally {
      setResetting(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-medium text-slate-700">Access Denied</h2>
        <p className="text-slate-500">Only administrators can manage customers.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto" data-testid="customers-module">
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        className="mb-4 text-slate-600 hover:text-slate-900"
        onClick={() => navigate('/dashboard')}
      >
        <Home className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-800">Customer Management</h1>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 px-3 py-0.5 text-sm font-semibold tabular-nums"
              data-testid="customers-total-count"
              aria-label={`Total customers: ${customers.length}`}
            >
              {customers.length} {customers.length === 1 ? 'customer' : 'customers'}
            </span>
          </div>
          <p className="text-slate-500">Manage customer accounts and information</p>
        </div>
        <Button 
          className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
          onClick={openCreateDialog}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Create Customer
        </Button>
      </div>

      {success && !showCreateDialog && !showEditDialog && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
          {success}
        </div>
      )}

      {error && !showCreateDialog && !showEditDialog && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No customers found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => toggleSort('customer_id')}
                      className="inline-flex items-center gap-1.5 hover:text-cyan-600 transition-colors"
                      data-testid="sort-customer-id"
                    >
                      Customer ID
                      <SortIcon field="customer_id" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => toggleSort('name')}
                      className="inline-flex items-center gap-1.5 hover:text-cyan-600 transition-colors"
                      data-testid="sort-name"
                    >
                      Name
                      <SortIcon field="name" />
                    </button>
                  </TableHead>
                  <TableHead>Email / Username</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => toggleSort('created_at')}
                      className="inline-flex items-center gap-1.5 hover:text-cyan-600 transition-colors"
                      data-testid="sort-created"
                    >
                      Created
                      <SortIcon field="created_at" />
                    </button>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-mono text-sm text-slate-500">
                      {customer.customer_id || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 border border-slate-200 shrink-0">
                          <AvatarImage src={customer.avatar_url} />
                          <AvatarFallback className="bg-cyan-100 text-cyan-600">
                            {customer.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-slate-800 block truncate" data-testid={`customer-name-${customer.id}`}>
                          {customer.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-slate-600">{customer.email}</div>
                      {customer.username && (
                        <div className="text-xs text-slate-400">@{customer.username}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm" data-testid={`customer-last-login-${customer.id}`}>
                      {customer.last_login_at ? (
                        <span title={formatDate(customer.last_login_at)}>
                          {formatRelativeTime(customer.last_login_at)}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(customer.created_at).split(',')[0]}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleResetPassword(customer)}
                          disabled={resetting === customer.id}
                          title="Reset & reveal password"
                          data-testid={`reset-password-btn-${customer.id}`}
                        >
                          {resetting === customer.id ? (
                            <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                          ) : (
                            <KeyRound className="w-4 h-4 text-amber-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(customer)}
                        >
                          <Edit className="w-4 h-4 text-cyan-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Customer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Create New Customer</DialogTitle>
            <DialogDescription className="text-slate-500">
              Add a new customer to the system
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleCreateCustomer} className="space-y-4 mt-4">
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
              <Label className="text-slate-700">Customer ID</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="customer-unique-id-input"
                    value={newCustomer.customer_id}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, customer_id: e.target.value }))}
                    placeholder="CUS-00001"
                    className="pl-10 bg-white border-slate-200 font-mono"
                  />
                </div>
                <Button type="button" variant="outline" size="icon" onClick={fetchNextCustomerId}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Unique ID (auto-generated or custom)</p>
            </div>

            <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
              <Label className="text-slate-700">Username (for login)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                <Input
                  value={newCustomer.username}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  placeholder="johndoe"
                  className="pl-8 bg-white border-slate-200"
                />
              </div>
              <p className="text-xs text-slate-400">Customer can login with email or username</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Email <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com (leave blank if none)"
                  className="pl-10 bg-white border-slate-200"
                  data-testid="new-customer-email-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Either an email or a username is required — the customer needs one of them to login.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-cyan-50/40 border border-cyan-100 rounded-lg">
              <div className="space-y-1.5">
                <Label className="text-slate-700">Onsite hourly rate</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCustomer.onsite_hourly_rate}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, onsite_hourly_rate: e.target.value }))}
                    placeholder="125.00"
                    className="pl-7 bg-white border-slate-200"
                    data-testid="customer-onsite-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Remote hourly rate</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCustomer.remote_hourly_rate}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, remote_hourly_rate: e.target.value }))}
                    placeholder="95.00"
                    className="pl-7 bg-white border-slate-200"
                    data-testid="customer-remote-rate-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 sm:col-span-2 m-0">These rates auto-populate hourly labor on this client's work orders.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700">Password *</Label>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="random-pw" 
                    checked={useRandomPassword}
                    onCheckedChange={(checked) => {
                      setUseRandomPassword(checked);
                      if (checked) generateRandomPassword(false);
                    }}
                  />
                  <Label htmlFor="random-pw" className="text-xs text-slate-500 cursor-pointer">
                    Generate random
                  </Label>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={useRandomPassword ? "text" : "password"}
                    value={newCustomer.password}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="pl-10 bg-white border-slate-200"
                    required
                    minLength={6}
                  />
                </div>
                {useRandomPassword && (
                  <>
                    <Button type="button" variant="outline" size="icon" onClick={() => generateRandomPassword(false)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(newCustomer.password)}>
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </>
                )}
              </div>
              {useRandomPassword && newCustomer.password && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                  <strong>Save this password:</strong> <code className="font-mono bg-white px-1 rounded">{newCustomer.password}</code>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Customer'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Edit Customer</DialogTitle>
            <DialogDescription className="text-slate-500">
              Update customer information and credentials
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleUpdateCustomer} className="space-y-4 mt-4">
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
              <Label className="text-slate-700">Customer ID</Label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={editData.customer_id}
                  onChange={(e) => setEditData(prev => ({ ...prev, customer_id: e.target.value }))}
                  placeholder="CUS-00001"
                  className="pl-10 bg-white border-slate-200 font-mono"
                />
              </div>
            </div>

            <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
              <Label className="text-slate-700">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                <Input
                  value={editData.username}
                  onChange={(e) => setEditData(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  placeholder="johndoe"
                  className="pl-8 bg-white border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-cyan-50/40 border border-cyan-100 rounded-lg">
              <div className="space-y-1.5">
                <Label className="text-slate-700">Onsite hourly rate</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editData.onsite_hourly_rate}
                    onChange={(e) => setEditData(prev => ({ ...prev, onsite_hourly_rate: e.target.value }))}
                    placeholder="125.00"
                    className="pl-7 bg-white border-slate-200"
                    data-testid="customer-edit-onsite-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Remote hourly rate</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editData.remote_hourly_rate}
                    onChange={(e) => setEditData(prev => ({ ...prev, remote_hourly_rate: e.target.value }))}
                    placeholder="95.00"
                    className="pl-7 bg-white border-slate-200"
                    data-testid="customer-edit-remote-rate-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700">New Password (optional)</Label>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="edit-random-pw" 
                    checked={useRandomPassword}
                    onCheckedChange={(checked) => {
                      setUseRandomPassword(checked);
                      if (checked) generateRandomPassword(true);
                      else setEditData(prev => ({ ...prev, password: '' }));
                    }}
                  />
                  <Label htmlFor="edit-random-pw" className="text-xs text-slate-500 cursor-pointer">
                    Generate random
                  </Label>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={useRandomPassword ? "text" : "password"}
                    value={editData.password}
                    onChange={(e) => setEditData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Leave empty to keep current password"
                    className="pl-10 bg-white border-slate-200"
                    minLength={6}
                  />
                </div>
                {useRandomPassword && (
                  <>
                    <Button type="button" variant="outline" size="icon" onClick={() => generateRandomPassword(true)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(editData.password)}>
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </>
                )}
              </div>
              {useRandomPassword && editData.password && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                  <strong>Save this password:</strong> <code className="font-mono bg-white px-1 rounded">{editData.password}</code>
                </div>
              )}
              <p className="text-xs text-slate-400">Leave empty to keep the current password</p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="ghost" onClick={() => {
                setShowEditDialog(false);
                setEditingCustomer(null);
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
                disabled={editing}
              >
                {editing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Customer'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Reveal Dialog */}
      <Dialog open={!!resetResult} onOpenChange={(open) => !open && setResetResult(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <KeyRound className="w-5 h-5 text-amber-600" />
              New Password Generated
            </DialogTitle>
            <DialogDescription>
              This password is shown <strong>only once</strong>. Copy it now and share it with the customer through a secure channel.
            </DialogDescription>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-slate-600 space-y-1">
                <div><strong>Customer:</strong> {resetResult.customer.name}</div>
                <div><strong>Email:</strong> {resetResult.customer.email}</div>
                {resetResult.customer.username && (
                  <div><strong>Username:</strong> {resetResult.customer.username}</div>
                )}
              </div>
              <div className="relative" data-testid="revealed-password-block">
                <Input
                  value={resetResult.new_password}
                  readOnly
                  className="font-mono bg-amber-50 border-amber-300 text-amber-900 pr-24"
                />
                <Button
                  type="button"
                  size="sm"
                  className="absolute right-1 top-1 h-8 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                  onClick={() => copyToClipboard(resetResult.new_password)}
                  data-testid="copy-password-btn"
                >
                  {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Button
                type="button"
                className="w-full bg-slate-700 hover:bg-slate-800 text-white rounded-full"
                onClick={() => setResetResult(null)}
                data-testid="close-reveal-btn"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
